import { useEffect, useState, useCallback } from "react";
import { Download, Trash2, ShieldCheck, AlertTriangle, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const LABELS = {
  applications: "Applications", user_accounts: "User accounts", contact_messages: "Contact messages",
  sos_tickets: "SOS tickets", payment_transactions: "Payment transactions",
  progress_records: "Progress records", nurture_emails: "Queued nurture emails", module_grants: "Module overrides",
};

export default function AdminPurge() {
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [purging, setPurging] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/purge-preview");
      setPreview(data);
    } catch {
      toast.error("Failed to load test-data preview");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleTestMode = async () => {
    try {
      const { data } = await api.post("/admin/test-mode", { enabled: !preview.test_data_mode });
      toast.success(data.test_data_mode ? "Test mode ON — new records will be marked as test data" : "Test mode OFF");
      load();
    } catch {
      toast.error("Toggle failed");
    }
  };

  const exportBackup = async () => {
    try {
      const { data } = await api.get("/admin/export-test-data");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `test-data-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Backup downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const purge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/purge-test-data", { phrase });
      setResult(data.deleted);
      setConfirming(false);
      setPhrase("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  if (!preview) return <p className="text-slate-500 text-sm">Loading…</p>;
  const total = Object.values(preview.summary).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="admin-purge" className="max-w-3xl space-y-6">
      <div className="bg-charcoal border border-white/10 p-6">
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety mb-2 flex items-center gap-2">
              <FlaskConical size={13} /> [TEST DATA MODE]
            </p>
            <p className="text-slate-300 text-sm leading-relaxed">
              While ON, every new application, contact message, and member record is marked as test data.
              Turn it on before end-to-end testing, off when real applicants may arrive. You can also mark
              any individual application as test from its dossier in the pipeline.
            </p>
          </div>
          <button onClick={toggleTestMode} data-testid="test-mode-toggle"
            className={`shrink-0 font-mono text-xs uppercase tracking-wider px-4 py-2.5 border transition-colors ${
              preview.test_data_mode ? "bg-safety/15 text-safety border-safety/50" : "border-white/15 text-slate-400 hover:border-white/40"
            }`}>
            {preview.test_data_mode ? "On" : "Off"}
          </button>
        </div>
      </div>

      <div className="bg-charcoal border border-white/10 p-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety mb-4">[MARKED TEST RECORDS]</p>
        {total === 0 ? (
          <p className="text-slate-500 text-sm" data-testid="purge-empty">No records are marked as test data. Nothing to purge.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-5" data-testid="purge-summary">
              {Object.entries(preview.summary).filter(([, v]) => v > 0).map(([k, v]) => (
                <div key={k} className="bg-navy p-4">
                  <p className="font-display font-extrabold text-white text-2xl">{v}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">{LABELS[k] || k}</p>
                </div>
              ))}
            </div>
            <div className="space-y-px border border-white/10 max-h-56 overflow-y-auto dark-scroll">
              {preview.records.map((r, i) => (
                <div key={i} className="bg-navy px-4 py-2.5 text-sm flex justify-between gap-4">
                  <span className="text-slate-300">{r.name} — {r.business}</span>
                  <span className="font-mono text-xs text-slate-500">{r.email}</span>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="mt-5 flex items-start gap-3 bg-emerald-500/5 border border-emerald-500/20 p-4">
          <ShieldCheck size={16} className="text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-xs text-slate-400 leading-relaxed">{preview.protected}</p>
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={exportBackup} disabled={total === 0} data-testid="purge-export-btn"
            className="border border-white/20 hover:border-safety disabled:opacity-40 text-white font-semibold px-5 py-3 text-sm flex items-center gap-2 transition-colors">
            <Download size={15} /> Export backup (.json)
          </button>
          <button onClick={() => setConfirming(true)} disabled={total === 0} data-testid="purge-start-btn"
            className="bg-red-500/15 text-red-400 border border-red-500/40 hover:bg-red-500/25 disabled:opacity-40 font-semibold px-5 py-3 text-sm flex items-center gap-2 transition-colors">
            <Trash2 size={15} /> Purge test data
          </button>
        </div>
      </div>

      {result && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 p-6" data-testid="purge-result">
          <p className="font-display font-bold text-emerald-400">Purge complete</p>
          <p className="text-slate-300 text-sm mt-2 font-mono">
            {Object.entries(result).map(([k, v]) => `${LABELS[k] || k}: ${v}`).join("  ·  ") || "Nothing deleted"}
          </p>
        </div>
      )}

      {confirming && (
        <div className="fixed inset-0 z-50 grid place-items-center p-5" data-testid="purge-confirm-modal">
          <div className="absolute inset-0 bg-navy/80 backdrop-blur-sm" onClick={() => setConfirming(false)} />
          <div className="relative bg-charcoal border border-red-500/40 max-w-md w-full p-7">
            <AlertTriangle size={28} className="text-red-400" />
            <h3 className="font-display font-extrabold text-white text-xl mt-4">Purge {total} test records?</h3>
            <p className="text-slate-400 text-sm mt-3 leading-relaxed">
              This permanently deletes only records marked as test data. Export a backup first if you want a copy.
              Type <span className="font-mono text-red-400">DELETE TEST DATA</span> to confirm.
            </p>
            <input value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="purge-phrase-input"
              placeholder="DELETE TEST DATA"
              className="w-full mt-5 bg-navy border border-white/15 text-white px-4 py-3 font-mono text-sm focus:border-red-400 outline-none" />
            <div className="flex gap-3 mt-5">
              <button onClick={purge} disabled={phrase !== "DELETE TEST DATA" || purging} data-testid="purge-confirm-btn"
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 text-sm transition-colors">
                {purging ? "Purging…" : "Delete permanently"}
              </button>
              <button onClick={() => { setConfirming(false); setPhrase(""); }} data-testid="purge-cancel-btn"
                className="border border-white/20 text-slate-300 hover:text-white px-5 py-3 text-sm transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
